// API base URL — empty string means same-origin requests.
//
// Dev: vite dev server proxies /api -> localhost:8787 (vite.config.js).
// Prod: Pages Function at functions/api/[[path]].js proxies /api -> Worker.
//
// Either way, frontend code calls relative paths like '/api/auth/login'
// and the appropriate proxy layer handles routing. Don't hardcode the
// Worker URL here — that breaks the proxy chain and triggers CORS.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

const CALM_MESSAGES: Record<number, string> = {
  429: 'API rate limit hit.',
  401: 'Your session expired. Please log in again.',
  400: 'Something was wrong with that request. Try regenerating.',
  500: 'Something went wrong on our end. Try again in a moment.',
  503: 'The server is temporarily unavailable. Try again in a moment.',
};

function calmMessage(status: number, serverMsg?: string): string {
  if (status === 429) return CALM_MESSAGES[429];
  if (status === 401) return CALM_MESSAGES[401];
  if (serverMsg && /network|fetch|failed to fetch/i.test(serverMsg)) {
    return "Can't reach the server. Check your connection and try again.";
  }
  // The worker writes user-facing error messages (e.g. naming WHICH related
  // word failed and in which phase). Blanket-replacing every 500 with
  // "Something went wrong on our end" hid that detail (2026-07-05 fix) —
  // prefer the server's message whenever one exists.
  if (serverMsg) return serverMsg;
  if (CALM_MESSAGES[status]) return CALM_MESSAGES[status];
  return `That didn't work (${status}). Try again in a moment.`;
}

class ApiError extends Error {
  status: number;
  data: unknown;
  retryAfter: number | null;

  constructor(message: string, status: number, data: unknown, retryAfter: number | null) {
    super(message);
    this.status = status;
    this.data = data;
    this.retryAfter = retryAfter;
  }
}

class ApiClient {
  token: string | null;

  constructor() {
    this.token = localStorage.getItem('waw_session_token');
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('waw_session_token', token);
    } else {
      localStorage.removeItem('waw_session_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  async request(path: string, options: RequestInit = {}): Promise<unknown> {
    const url = `${API_BASE}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    let response: Response;
    try {
      response = await fetch(url, { ...options, headers });
    } catch (err) {
      throw new ApiError(
        "Can't reach the server. Check your connection and try again.",
        0, null, null
      );
    }

    const isAuthEndpoint = path === '/api/auth/login' || path === '/api/auth/logout';

    if (response.status === 401 && !isAuthEndpoint) {
      this.setToken(null);
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }

    let data: unknown = await response.json().catch(() => null);

    // Silent JSON parse retry: if the body was unparseable and the response was
    // ok, retry once — but ONLY for idempotent GETs. Re-issuing a POST/PUT/DELETE
    // here meant a 2xx whose body just failed to parse triggered a SECOND
    // mutation (a duplicate paid generation / double write). Non-GET methods now
    // surface the parse error without a retry (2026-07-05 M2 fix).
    const method = (options.method || 'GET').toUpperCase();
    const isIdempotent = method === 'GET';
    if (data === null && response.ok && isIdempotent) {
      try {
        const retry = await fetch(url, { ...options, headers });
        data = await retry.json().catch(() => null);
        if (data === null) {
          throw new ApiError(
            "The AI returned something we couldn't read. Try regenerating.",
            response.status, null, null
          );
        }
        return data;
      } catch (err) {
        if (err instanceof ApiError) throw err;
        throw new ApiError(
          "The AI returned something we couldn't read. Try regenerating.",
          response.status, null, null
        );
      }
    }

    if (!response.ok) {
      const retryAfter = response.status === 429
        ? parseInt(response.headers.get('Retry-After') || '0', 10) || null
        : null;

      const serverMsg = (data as { error?: string })?.error;
      const message = isAuthEndpoint && serverMsg
        ? serverMsg
        : calmMessage(response.status, serverMsg);

      throw new ApiError(
        message,
        response.status,
        data,
        retryAfter
      );
    }

    return data;
  }

  get(path: string): Promise<unknown> {
    return this.request(path);
  }

  post(path: string, body: unknown): Promise<unknown> {
    return this.request(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  put(path: string, body: unknown): Promise<unknown> {
    return this.request(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  delete(path: string): Promise<unknown> {
    return this.request(path, { method: 'DELETE' });
  }

  // Raw bytes (a template, a pose image, a final slide) with the session token.
  // The Social panel needs them; JSON routes keep using request().
  async getBytes(path: string): Promise<{ bytes: ArrayBuffer; type: string }> {
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, { headers });
    } catch {
      throw new ApiError("Can't reach the server. Check your connection and try again.", 0, null, null);
    }
    if (response.status === 401) {
      this.setToken(null);
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new ApiError(calmMessage(response.status, (data as { error?: string })?.error), response.status, data, null);
    }
    return { bytes: await response.arrayBuffer(), type: response.headers.get('Content-Type') || '' };
  }
}

export const api = new ApiClient();
export { ApiError };
